// JIE JIANG redesign (2026-10-29): "Show more" for the museum listing (main-collection layout_type 'museum').
// snippets/product-grid.liquid renders the footer under the list: "Showing x of y", a thin bar and Show more,
// a plain link to the next page (without JS it simply opens that page). Here the click fetches that page of the
// same section through the theme's section renderer (Horizon's paginated list has usually prefetched page 2 into
// the same cache), appends its rows to the list, takes the count, bar width and next link from the fetched
// footer (the link is hidden on the last page), announces the new count and moves focus to the first new row.
// The address bar is left alone (no ?page=N). The next page is built from the current URL, so filter.* / sort_by
// stay; a filter or sort change re-renders the section at page 1 and drops the appended rows (and any request
// still in flight for the old list is ignored).
// Judge.me: card badges of products with no reviews are marked done so Judge.me skips them (appended rows and
// every re-render, which rewrites the class) - the rule of assets/jj-atelier-browse.js (skipZeroReviewBadges).
// Back: each append records the last loaded page in this history entry's state (jjMorePage, beside the
// scrollTop that Horizon's scroll-container saves there at pagehide); on load the missing pages are appended
// quietly and that scrollTop is applied again, so a visitor returning from a product lands where they were.
// A reload appends the pages again but cannot restore the position (Chrome drops the pagehide replaceState then).
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';

const LINK = 'a[data-jj-more-link]';
const FOOTER = '[data-jj-more]';
const GRID = '[ref="grid"]';
const ROWS = ':scope > [ref="cards[]"]';
// Inner Judge.me badge of a card (snippets/product-card.liquid) whose product has no reviews yet, not yet marked.
const ZERO_REVIEWS = '.jdgm-preview-badge:not(.jdgm--done-setup) > .jdgm-prev-badge[data-number-of-reviews="0"]';

/** @param {ParentNode} root */
function skipZeroReviewBadges(root) {
  for (const inner of root.querySelectorAll(ZERO_REVIEWS)) inner.parentElement?.classList.add('jdgm--done-setup');
}

/**
 * The URL of a page of the current list, built like Horizon's paginated list (assets/paginated-list.js) so a
 * page it prefetched is found in the section renderer's cache.
 * @param {number} page
 */
function pageURL(page) {
  const url = new URL(window.location.href);
  url.searchParams.set('page', String(page));
  url.hash = '';
  return url;
}

/** The current list without its page: when it changes, filters or sort changed while a page was loading. */
function listKey() {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  url.searchParams.sort();
  return url.pathname + url.search;
}

/**
 * A next-page link taken from a section rendering response, without the section_id parameter, so opening it
 * in a new tab (or without JS later) shows the page and not the bare section.
 * @param {string} href
 */
function pageHref(href) {
  const url = new URL(href, window.location.href);
  url.searchParams.delete('section_id');
  return url.toString();
}

/**
 * @param {Element} footer - The live footer.
 * @param {Element | null} next - The footer of the fetched page.
 * @param {HTMLAnchorElement} link - The live Show more link.
 */
function updateFooter(footer, next, link, quiet = false) {
  const shown = footer.querySelector('[data-jj-more-shown]');
  const nextShown = next?.querySelector('[data-jj-more-shown]');
  if (shown && nextShown) shown.textContent = nextShown.textContent;

  const fill = footer.querySelector('[data-jj-more-fill]');
  const nextFill = next?.querySelector('[data-jj-more-fill]');
  if (fill instanceof HTMLElement && nextFill instanceof HTMLElement) fill.style.width = nextFill.style.width;

  const nextLink = next?.querySelector(LINK);
  const nextHref = nextLink?.getAttribute('href');
  if (nextHref) {
    link.href = pageHref(nextHref);
    link.hidden = false;
  } else {
    link.hidden = true;
  }

  const status = footer.querySelector('[data-jj-more-status]');
  if (status && shown && !quiet) {
    // Emptied, then refilled a moment later, so assistive technology announces it even when the text repeats
    // (a timer, not an animation frame: frames wait while the tab is in the background).
    status.textContent = '';
    setTimeout(() => {
      status.textContent = shown.textContent?.trim() ?? '';
    }, 100);
  }
}

/** @returns {Record<string, unknown>} */
function historyState() {
  return history.state && typeof history.state === 'object' ? history.state : {};
}

/**
 * Loads the next page into the list.
 * @param {HTMLAnchorElement} link
 * @param {boolean} [quiet] - Restoring after back / reload: no focus move, announcement or prefetch, and no
 *   navigation on failure.
 * @returns {Promise<boolean>} false when the page structure is not the expected one (the link should navigate).
 */
async function showMore(link, quiet = false) {
  const list = link.closest('results-list');
  const footer = link.closest(FOOTER);
  const grid = list?.querySelector(GRID);
  const sectionId = list?.getAttribute('section-id');
  if (!list || !footer || !grid || !sectionId) return false;

  const rows = grid.querySelectorAll(ROWS);
  const last = /** @type {HTMLElement | undefined} */ (rows[rows.length - 1]);
  const page = Number(last?.dataset.page) + 1;
  if (!Number.isInteger(page) || page < 2) return false;

  const key = listKey();
  footer.setAttribute('data-loading', '');
  link.setAttribute('aria-disabled', 'true');
  grid.setAttribute('aria-busy', 'true');

  try {
    const html = await sectionRenderer.getSectionHTML(sectionId, true, pageURL(page));
    // Superseded: the list was re-rendered for other filters / sort, or the section was replaced.
    if (!link.isConnected || !grid.isConnected || listKey() !== key) return true;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fetched = doc.querySelector(GRID);
    const items = fetched
      ? /** @type {HTMLElement[]} */ ([...fetched.querySelectorAll(ROWS)]).filter(
          (item) => !item.id || !document.getElementById(item.id)
        )
      : [];
    if (!items.length) {
      // Not a page of this list (error page, empty response): open the page itself instead.
      if (quiet) return false;
      window.location.assign(link.href);
      return true;
    }

    for (const item of items) {
      // The fetched page's first row is eager there; appended below the fold here, so every image waits.
      for (const img of item.querySelectorAll('img')) {
        img.loading = 'lazy';
        if (img.sizes && !img.sizes.startsWith('auto')) img.sizes = `auto, ${img.sizes}`;
      }
      skipZeroReviewBadges(item);
    }

    grid.append(...items);
    updateFooter(footer, doc.querySelector(FOOTER), link, quiet);
    try {
      history.replaceState({ ...historyState(), jjMorePage: page, jjMoreKey: key }, '');
    } catch {
      // the state object is over the browser's size limit: back simply shows the first page
    }
    if (quiet) return true;

    // The new rows start where the link was, so focus moves without scrolling.
    const first = items[0]?.querySelector('a.product-card__link') ?? items[0]?.querySelector('a[href]');
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });

    if (!link.hidden) {
      requestIdleCallback(() => {
        sectionRenderer.getSectionHTML(sectionId, true, pageURL(page + 1)).catch(() => {});
      });
    }
    return true;
  } catch {
    if (quiet) return false;
    if (link.isConnected && listKey() === key) window.location.assign(link.href);
    return true;
  } finally {
    footer.removeAttribute('data-loading');
    link.removeAttribute('aria-disabled');
    grid.removeAttribute('aria-busy');
  }
}

/** @type {WeakSet<HTMLAnchorElement>} */
const busy = new WeakSet();

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target instanceof Element ? event.target.closest(LINK) : null;
  if (!(link instanceof HTMLAnchorElement)) return;

  event.preventDefault();
  if (busy.has(link) || link.closest(FOOTER)?.hasAttribute('data-loading')) return;
  busy.add(link);
  showMore(link)
    .then((handled) => {
      if (!handled) window.location.assign(link.href);
    })
    .finally(() => busy.delete(link));
});

/** Back / reload into a list that had pages appended: append them again, then put the scroll position back. */
async function restoreAppended() {
  const state = historyState();
  const want = Number(state.jjMorePage);
  if (!(want > 1) || state.jjMoreKey !== listKey()) return;
  for (let guard = 0; guard < want; guard++) {
    const link = document.querySelector(`results-list ${LINK}`);
    if (!(link instanceof HTMLAnchorElement) || link.hidden) break;
    const rows = link.closest('results-list')?.querySelectorAll(`${GRID} > [ref="cards[]"]`);
    const last = /** @type {HTMLElement | undefined} */ (rows?.[rows.length - 1]);
    if (Number(last?.dataset.page) >= want) break;
    if (!(await showMore(link, true))) break;
  }
  const top = Number(historyState().scrollTop);
  if (!Number.isFinite(top) || top <= 0) return;
  const wrapper = document.querySelector('.page-wrapper');
  const scroller =
    window.matchMedia('(min-width: 990px)').matches && wrapper ? wrapper : document.scrollingElement;
  // Horizon's own restore (pageshow) may still run after this with the same value; both are instant.
  requestAnimationFrame(() => scroller?.scrollTo({ top, behavior: 'instant' }));
}
/**
 * Opened at ?page=N (a search result, a shared link, Show more without JS): load the earlier pages above it, so
 * the list matches its count ("Showing 16 of 20") and the numbering starts at 01. A visit at the top of the page
 * stays at the top (no jump on its own); if the visitor scrolled while the pages loaded, the rows they look at
 * stay where they are; back puts the saved scroll position back once the pages are in (not a reload:
 * history.state.scrollTop is lost then).
 */
async function prependEarlier() {
  const list = document.querySelector('results-list');
  const grid = list?.querySelector(GRID);
  const footer = list?.querySelector(FOOTER);
  const sectionId = list?.getAttribute('section-id');
  if (!grid || !footer || !sectionId) return;
  const firstRow = /** @type {HTMLElement | null} */ (grid.querySelector(ROWS));
  let page = Number(firstRow?.dataset.page);
  if (!(page > 1)) return;
  const key = listKey();
  const wrapper = document.querySelector('.page-wrapper');
  const scroller = () =>
    window.matchMedia('(min-width: 990px)').matches && wrapper ? wrapper : document.scrollingElement;
  // Back (not reload): the saved scrollTop was measured with these pages already in the list, so it is applied again
  // once they are back instead of compensating each insert.
  const saved = Number(historyState().scrollTop);
  const restoring = historyState().scrollTop != null && Number.isFinite(saved);
  footer.setAttribute('data-loading', '');
  try {
    while (page > 1) {
      page -= 1;
      const html = await sectionRenderer.getSectionHTML(sectionId, true, pageURL(page));
      if (!grid.isConnected || listKey() !== key) return;
      const fetched = new DOMParser().parseFromString(html, 'text/html').querySelector(GRID);
      const items = fetched
        ? /** @type {HTMLElement[]} */ ([...fetched.querySelectorAll(ROWS)]).filter(
            (item) => !item.id || !document.getElementById(item.id)
          )
        : [];
      if (!items.length) return;
      for (const item of items) {
        for (const img of item.querySelectorAll('img')) {
          img.loading = 'lazy';
          if (img.sizes && !img.sizes.startsWith('auto')) img.sizes = `auto, ${img.sizes}`;
        }
        skipZeroReviewBadges(item);
      }
      const sc = scroller();
      // A fresh visit at the top stays at the top; one the visitor scrolled keeps the rows they look at.
      const hold = !restoring && (sc?.scrollTop ?? 0) > 0;
      const anchor = grid.querySelector(ROWS);
      const before = anchor?.getBoundingClientRect().top ?? 0;
      // The browser's own scroll anchoring is off while the rows go in, so nothing is compensated twice.
      const unanchored = [sc, document.documentElement, document.body].filter(
        (el) => el instanceof HTMLElement
      );
      const kept = unanchored.map((el) => el.style.overflowAnchor);
      for (const el of unanchored) el.style.overflowAnchor = 'none';
      grid.prepend(...items);
      const offset = Number(/jj-mu\s+(\d+)/.exec(grid.style.counterReset)?.[1]) || 0;
      grid.style.counterReset = `jj-mu ${Math.max(0, offset - items.length)}`;
      const shift = (anchor?.getBoundingClientRect().top ?? before) - before;
      if (hold && shift) sc?.scrollBy({ top: shift, behavior: 'instant' });
      unanchored.forEach((el, i) => (el.style.overflowAnchor = kept[i] ?? ''));
    }
    if (restoring && saved > 0) scroller()?.scrollTo({ top: saved, behavior: 'instant' });
  } catch {
    // the earlier pages stay out; the page itself is complete
  } finally {
    footer.removeAttribute('data-loading');
  }
}

prependEarlier().then(restoreAppended);

// Zero-review badges: the rows on the page now, and every later change (appended rows; a filter / sort re-render
// brings new cards and rewrites the class of the kept ones).
for (const grid of document.querySelectorAll('.product-grid--museum')) {
  const list = grid.closest('results-list');
  if (!list) continue;
  skipZeroReviewBadges(list);
  new MutationObserver(() => skipZeroReviewBadges(list)).observe(list, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class'],
  });
}
